import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, EchecApi } from '../partage/api/client';
import { viderLaFile } from '../partage/api/fileHorsLigne';
import {
  CE_QUI_PARTIRA,
  COMPTE_SUPPRIME,
  GARDER_MON_COMPTE,
  REFUS_DU_DERNIER_COMPTE,
  REFUS_DU_PREMIER_COMPTE,
  REFUS_SANS_RAISON,
  REVENIR_A_LA_PRESENTATION,
  REVENIR_A_L_APP,
  SUPPRIMER_DEFINITIVEMENT,
  SUPPRIMER_MON_COMPTE_QUESTION,
  TOUT_EST_PARTI,
  UN_INSTANT,
} from '../partage/vocabulaire';
import { AccueilSansCompte, ChampEmail, ChampMotDePasse } from './AccueilSansCompte';
import { raisonDuRefusDeSuppression, type RaisonDuRefus } from './suppression';
import './Connexion.css';

/**
 * Les écrans des mails (chantier mails), sur la feuille scotchée par-dessus la vitrine, comme la
 * maquette `.scratch/chantiers/mails/maquettes/connexion.html` (sections 2 et 3) : « Regarde ta
 * boîte mail », « Mot de passe oublié », « C'est parti », et les deux pages où mènent les liens
 * des mails, « Adresse vérifiée » et « Nouveau mot de passe ».
 */

/** La règle du serveur (`server/src/domain/comptes/inscription.ts`), redite ici pour ne pas gâcher un essai. */
const MOT_DE_PASSE_LONGUEUR_MIN = 8;

const PAS_DE_REPONSE = 'Quelque chose n’a pas répondu. Réessaie.';

function messageDEchec(erreur: unknown): string {
  if (!(erreur instanceof EchecApi)) return PAS_DE_REPONSE;
  if (erreur.code === 'hors_ligne') return 'Pas de réseau. Ce geste a besoin d’en avoir.';
  return erreur.statut === 429 ? 'Trop de tentatives. Laisse passer quelques minutes.' : PAS_DE_REPONSE;
}

function Enveloppe() {
  return (
    <div className="connexion-enveloppe" aria-hidden="true">
      <svg viewBox="0 0 92 64">
        <path d="M4 6 L46 38 L88 6" fill="none" stroke="#c9b48c" strokeWidth="2.5" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/**
 * « Regarde ta boîte mail » : après l'inscription, ou quand un compte neuf essaie d'entrer avant
 * d'avoir ouvert le lien. « Renvoyer le mail » ne dit jamais si un mail est parti : le serveur
 * attend une minute entre deux liens, et répond toujours pareil.
 */
export function BoiteMail({ email, onRetour }: { email: string; onRetour: () => void }) {
  const [renvoye, setRenvoye] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function renvoyer() {
    if (enCours) return;
    setEnCours(true);
    setRefus(null);
    try {
      await api.renvoyerVerification(email);
      setRenvoye(true);
    } catch (erreur) {
      setRefus(messageDEchec(erreur));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <AccueilSansCompte titre="Regarde ta boîte mail" description="" centre>
      <Enveloppe />
      <p className="connexion-description">
        Un lien vient de partir vers <b>{email.trim()}</b>. Clique-le pour ouvrir ton carnet. Il vaut 48 heures.
      </p>
      <p className="connexion-refus">
        {renvoye
          ? 'C’est reparti. Le nouveau lien remplace l’ancien ; attends une minute avant de redemander.'
          : 'Rien reçu ? Regarde dans les indésirables, ou vérifie l’adresse.'}
      </p>
      <button type="button" className="connexion-second" onClick={renvoyer} disabled={enCours}>
        {enCours ? 'Un instant' : 'Renvoyer le mail'}
      </button>
      {refus && (
        <p className="refus connexion-refus" role="status">
          {refus}
        </p>
      )}
      <button type="button" className="connexion-lien" onClick={onRetour}>
        Ce n&apos;est pas mon adresse
      </button>
    </AccueilSansCompte>
  );
}

/** « Mot de passe oublié » puis « C'est parti » : la même phrase, que le compte existe ou non. */
export function MotDePasseOublie({ onRetour }: { onRetour: () => void }) {
  const [email, setEmail] = useState('');
  const [parti, setParti] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    if (email.trim() === '' || enCours) return;
    setEnCours(true);
    setRefus(null);
    try {
      await api.motDePasseOublie(email);
      setParti(true);
    } catch (erreur) {
      setRefus(messageDEchec(erreur));
    } finally {
      setEnCours(false);
    }
  }

  if (parti) {
    return (
      <AccueilSansCompte titre="C'est parti" description="" centre>
        <Enveloppe />
        <p className="connexion-description">
          Si un compte existe avec cette adresse, un mail vient de partir. Le lien vaut une heure.
        </p>
        <button type="button" className="connexion-lien" onClick={onRetour}>
          Revenir à la connexion
        </button>
      </AccueilSansCompte>
    );
  }

  return (
    <AccueilSansCompte titre="Mot de passe oublié" description="Tape ton adresse : on t’envoie un lien pour en choisir un nouveau.">
      <form className="connexion-carte" onSubmit={soumettre}>
        <label className="connexion-etiquette" htmlFor="email">
          E-mail
        </label>
        <ChampEmail id="email" valeur={email} onChange={setEmail} />
        <button className="connexion-entrer" type="submit" disabled={email.trim() === '' || enCours}>
          {enCours ? 'Un instant' : 'Envoyer le lien'}
        </button>
        {refus && (
          <p className="refus connexion-refus" role="status">
            {refus}
          </p>
        )}
      </form>
      <button type="button" className="connexion-lien" onClick={onRetour}>
        Revenir à la connexion
      </button>
    </AccueilSansCompte>
  );
}

/** Les pages où mènent les liens des mails, connecté ou non. */
export const CHEMINS_DES_LIENS = ['/verifier-adresse', '/nouveau-mot-de-passe', '/supprimer-mon-compte'] as const;
export type CheminDUnLien = (typeof CHEMINS_DES_LIENS)[number];

export function cheminDUnLien(chemin: string): CheminDUnLien | null {
  return (CHEMINS_DES_LIENS as readonly string[]).includes(chemin) ? (chemin as CheminDUnLien) : null;
}

/**
 * Le jeton du lien, lu après le `#` (il n'est jamais allé au serveur en ouvrant la page), puis
 * retiré de l'adresse : il ne reste ni dans l'historique, ni dans un favori. Lu une seule fois,
 * même quand React monte l'écran deux fois.
 */
function useJetonDuLien(): string {
  const [jeton] = useState(() => window.location.hash.replace(/^#/, ''));
  useEffect(() => {
    if (window.location.hash !== '') window.history.replaceState(null, '', window.location.pathname);
  }, []);
  return jeton;
}

/** Un lien périmé, déjà servi ou remplacé : une phrase claire, et le chemin pour en redemander un. */
function LienQuiNeVautPlus({ perime, onRedemander, redemander }: { perime: boolean; onRedemander: () => void; redemander: string }) {
  return (
    <AccueilSansCompte titre="Ce lien ne vaut plus" description="" centre>
      <p className="connexion-description">
        {perime ? 'Il a dépassé son temps.' : 'Il a déjà servi, ou un lien plus récent l’a remplacé.'} Demande-en un nouveau.
      </p>
      <button type="button" className="connexion-entrer connexion-plein" onClick={onRedemander}>
        {redemander}
      </button>
    </AccueilSansCompte>
  );
}

/** Le refus d'un lien, lu dans l'échec : vrai si périmé, faux s'il ne vaut pas ; null pour une autre panne. */
function refusDuLien(erreur: unknown): { perime: boolean } | null {
  if (!(erreur instanceof EchecApi)) return null;
  if (erreur.code === 'lien_perime') return { perime: true };
  if (erreur.code === 'lien_inconnu') return { perime: false };
  return null;
}

const entrerDansLApp = () => window.location.assign('/');

/** `/verifier-adresse#…` : l'adresse est vérifiée dès l'ouverture, puis « Entrer ». */
export function AdresseVerifiee() {
  const jeton = useJetonDuLien();
  const [etat, setEtat] = useState<'en_cours' | 'verifiee' | { perime: boolean } | { panne: string }>('en_cours');
  const parti = useRef(false);

  useEffect(() => {
    if (parti.current) return;
    parti.current = true;
    api.verifierAdresse(jeton).then(
      () => setEtat('verifiee'),
      (erreur: unknown) => setEtat(refusDuLien(erreur) ?? { panne: messageDEchec(erreur) })
    );
  }, [jeton]);

  if (etat === 'en_cours') return <AccueilSansCompte titre="Un instant" description="" centre>{null}</AccueilSansCompte>;
  if (etat === 'verifiee') {
    return (
      <AccueilSansCompte titre="Adresse vérifiée" description="C’est bon, ton carnet est ouvert." centre>
        <button type="button" className="connexion-entrer connexion-plein" onClick={entrerDansLApp} autoFocus>
          Entrer
        </button>
      </AccueilSansCompte>
    );
  }
  if ('panne' in etat) {
    return (
      <AccueilSansCompte titre="Adresse à vérifier" description={etat.panne} centre>
        <button type="button" className="connexion-entrer connexion-plein" onClick={() => window.location.reload()}>
          Réessayer
        </button>
      </AccueilSansCompte>
    );
  }
  // Un nouveau lien se demande depuis la connexion : entrer avant d'avoir vérifié mène à
  // « Regarde ta boîte mail », et à « Renvoyer le mail ».
  return <LienQuiNeVautPlus perime={etat.perime} onRedemander={() => window.location.assign('/connexion')} redemander="Revenir à la connexion" />;
}

/** `/nouveau-mot-de-passe#…` : le nouveau mot de passe, puis l'app. Les autres connexions sont fermées. */
export function NouveauMotDePasse() {
  const jeton = useJetonDuLien();
  const [motDePasse, setMotDePasse] = useState('');
  const [refus, setRefus] = useState<string | null>(null);
  const [lien, setLien] = useState<{ perime: boolean } | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function soumettre(evenement: FormEvent) {
    evenement.preventDefault();
    if (motDePasse === '' || enCours) return;
    if ([...motDePasse].length < MOT_DE_PASSE_LONGUEUR_MIN) {
      setRefus(`Le mot de passe doit faire au moins ${MOT_DE_PASSE_LONGUEUR_MIN} caractères.`);
      return;
    }
    setEnCours(true);
    setRefus(null);
    try {
      await api.nouveauMotDePasse(jeton, motDePasse);
      entrerDansLApp();
    } catch (erreur) {
      const refusLien = refusDuLien(erreur);
      if (refusLien) setLien(refusLien);
      else setRefus(messageDEchec(erreur));
      setEnCours(false);
    }
  }

  if (lien) {
    return <LienQuiNeVautPlus perime={lien.perime} onRedemander={() => window.location.assign('/mot-de-passe-oublie')} redemander="Demander un nouveau lien" />;
  }

  return (
    <AccueilSansCompte titre="Nouveau mot de passe" description="Choisis-en un nouveau. Tes autres connexions seront fermées.">
      <form className="connexion-carte" onSubmit={soumettre}>
        <label className="connexion-etiquette" htmlFor="mot-de-passe">
          Nouveau mot de passe
        </label>
        <ChampMotDePasse id="mot-de-passe" valeur={motDePasse} onChange={setMotDePasse} autoComplete="new-password" autoFocus />
        <p className="connexion-aide">Au moins {MOT_DE_PASSE_LONGUEUR_MIN} caractères.</p>
        <button className="connexion-entrer" type="submit" disabled={motDePasse === '' || enCours}>
          {enCours ? 'Un instant' : 'Enregistrer et entrer'}
        </button>
        {refus && (
          <p className="refus connexion-refus" role="status">
            {refus}
          </p>
        )}
      </form>
    </AccueilSansCompte>
  );
}

const RAISONS_DU_REFUS: Record<RaisonDuRefus, string> = {
  premier: REFUS_DU_PREMIER_COMPTE,
  dernier: REFUS_DU_DERNIER_COMPTE,
  inconnue: REFUS_SANS_RAISON,
};

/**
 * `/supprimer-mon-compte#…` (chantier parametres-du-compte, ticket 03), connecté ou non : le lien
 * du mail n'efface rien tout seul (des boîtes mail ouvrent les liens pour les vérifier), il
 * demande une dernière fois. « Garder mon compte » ramène à l'app, ou à la vitrine sans session.
 * Après : la file de capture de l'appareil est vidée, comme à « Sortir », et la vitrine.
 */
export function SupprimerMonCompte() {
  const jeton = useJetonDuLien();
  const [etat, setEtat] = useState<'question' | 'en_cours' | 'supprime' | { perime: boolean } | { refus: RaisonDuRefus } | { panne: string }>(
    'question'
  );

  async function supprimer() {
    if (etat === 'en_cours') return;
    setEtat('en_cours');
    try {
      await api.supprimerLeCompte(jeton);
      await viderLaFile().catch(() => undefined);
      setEtat('supprime');
    } catch (erreur) {
      const refus = raisonDuRefusDeSuppression(erreur);
      setEtat(refus ? { refus } : (refusDuLien(erreur) ?? { panne: messageDEchec(erreur) }));
    }
  }

  if (etat === 'supprime') {
    return (
      <AccueilSansCompte titre={COMPTE_SUPPRIME} description={TOUT_EST_PARTI} centre>
        <button type="button" className="connexion-entrer connexion-plein" onClick={() => window.location.assign('/vitrine/')} autoFocus>
          {REVENIR_A_LA_PRESENTATION}
        </button>
      </AccueilSansCompte>
    );
  }
  if (typeof etat === 'object' && 'perime' in etat) {
    // Un nouveau lien se demande depuis Paramètres, qui passe par la connexion sans session.
    return <LienQuiNeVautPlus perime={etat.perime} onRedemander={entrerDansLApp} redemander={REVENIR_A_L_APP} />;
  }

  return (
    <AccueilSansCompte titre={SUPPRIMER_MON_COMPTE_QUESTION} description="" centre>
      <p className="connexion-description">{CE_QUI_PARTIRA}</p>
      {typeof etat === 'object' && 'refus' in etat ? (
        <p className="refus connexion-refus" role="status">
          {RAISONS_DU_REFUS[etat.refus]}
        </p>
      ) : (
        <button type="button" className="connexion-entrer connexion-plein connexion-entrer--supprimer" onClick={supprimer} disabled={etat === 'en_cours'}>
          {etat === 'en_cours' ? UN_INSTANT : SUPPRIMER_DEFINITIVEMENT}
        </button>
      )}
      {typeof etat === 'object' && 'panne' in etat && (
        <p className="refus connexion-refus" role="status">
          {etat.panne}
        </p>
      )}
      <button type="button" className="connexion-second" onClick={entrerDansLApp} disabled={etat === 'en_cours'}>
        {GARDER_MON_COMPTE}
      </button>
    </AccueilSansCompte>
  );
}
